/**
 * App — top-level routing.
 *
 * React Router v7 (NOT v6). The v7 API uses `<Routes>`, `<Route>`, and
 * `element=` props familiar from v6 but with strictly-typed loaders and
 * actions when used. We are not using loaders here — those land per
 * feature epic.
 *
 * Stubbed routes render <NotImplemented /> until their owning story
 * lands. Replace inline in the appropriate later story.
 *
 * STORY-001-009: the auth screens (signup, login, verify-email,
 * forgot-password, reset-password, oauth handoff) are wired below in
 * place of the original `NotImplemented` stubs. The `/account/*` stub
 * is now wrapped in `RequireAuth` per AC #15 — STORY-001-010 will
 * replace the inner stub with the real Account UI.
 */
import { useEffect, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";

import { AppShell } from "@/app/AppShell";
import { LandingV2 } from "@/features/landing/v2/LandingV2";
import { NotFound } from "@/app/NotFound";
import { RedirectIfAuthenticated } from "@/app/RedirectIfAuthenticated";
import { RequireAuth } from "@/app/RequireAuth";
import { AccountLayout } from "@/features/account/AccountLayout";
import { PrivacyScreen } from "@/features/account/PrivacyScreen";
import { ProfileScreen } from "@/features/account/ProfileScreen";
import { SessionsScreen } from "@/features/account/SessionsScreen";
import { ChatEntryRoute, HomeRoute, SettingsEntryRoute } from "@/app/HomeRoute";
import { MarketplacePage } from "@/features/marketplace/MarketplacePage";
import { ProductDetailPage } from "@/features/marketplace/ProductDetailPage";
import { SearchResultsPage } from "@/features/marketplace/SearchResultsPage";
import { AuthLoginScreen } from "@/features/auth/AuthLoginScreen";
import { ForgotPasswordScreen } from "@/features/auth/ForgotPasswordScreen";
import { LoginScreen } from "@/features/auth/LoginScreen";
import { OAuthHandoffScreen } from "@/features/auth/OAuthHandoffScreen";
import { ResetPasswordScreen } from "@/features/auth/ResetPasswordScreen";
import { SignupScreen } from "@/features/auth/SignupScreen";
import { VerifyEmailScreen } from "@/features/auth/VerifyEmailScreen";

// Design-revamp screens (Phase D).
import { ArticleScreen, LibraryListScreen } from "@/features/library";
import { MaterialsScreen } from "@/features/materials";
import { OnboardingFlow } from "@/features/onboarding";
import { ShopsScreen } from "@/features/shops";
import { Navigate } from "react-router-dom";

// EPIC-006 — Seller registration wizard (Phase 6)
import { SellerRegistrationFlow } from "@/features/seller-registration/SellerRegistrationFlow";
// EPIC-006 — Seller claim page (Phase 6)
import { ClaimPage } from "@/features/seller-dashboard/ClaimPage";
// EPIC-006 — Admin shell (Phase 5)
import { AdminGuard } from "@/features/admin/AdminGuard";
import { AdminLayout } from "@/features/admin/AdminLayout";
import { AdminDashboardLanding } from "@/features/admin/AdminDashboardLanding";
import { AdminAccountPage } from "@/features/admin/account/AdminAccountPage";
import { AdminKitGallery } from "@/features/admin/_shared/AdminKitGallery";
import { AdminNotFound } from "@/features/admin/AdminNotFound";
import { RequirePermissionRoute } from "@/features/admin/RequirePermissionRoute";
import { QueueListPage } from "@/features/admin/seller-applications/QueueListPage";
// EPIC-008 — Admin marketplace management (STORY-008-007)
import { SellerListPage } from "@/features/admin/sellers/SellerListPage";
import { ProductListPage as AdminProductListPage } from "@/features/admin/products/ProductListPage";
import { ProductEditorPage as AdminProductEditorPage } from "@/features/admin/products/ProductEditorPage";
import { AppConfigPage } from "@/features/admin/app-config/AppConfigPage";
import { FeatureFlagsPage } from "@/features/admin/feature-flags/FeatureFlagsPage";
import { UserListPage } from "@/features/admin/users/UserListPage";
import { AuditLogPage } from "@/features/admin/audit/AuditLogPage";
// P1b admin-wave — marketplace/users management pages.
import { CategoryListPage } from "@/features/admin/categories/CategoryListPage";
import { MarketListPage } from "@/features/admin/markets/MarketListPage";
// PIM Wave P1 — attribute field library + category binding editor (marketplace.attribute.manage).
import { FieldLibraryPage } from "@/features/admin/attribute-fields/FieldLibraryPage";
import { BindingEditorPage } from "@/features/admin/category-bindings/BindingEditorPage";
import { BulkImportPage } from "@/features/admin/import/BulkImportPage";
import { InvitationsPage } from "@/features/admin/invitations/InvitationsPage";
// PIM Wave P3 Task 3 — admin product-review queue (marketplace.product.approve).
import { ProductReviewPage } from "@/features/admin/product-review/ProductReviewPage";
// PIM Wave P3 Task 4 — admin catalog-suggestions queue (marketplace.suggestion.review).
import { SuggestionsPage } from "@/features/admin/suggestions/SuggestionsPage";
// PIM Wave P4 Task 7 — admin need-mappings CRUD (marketplace.attribute.manage).
import { NeedMappingsPage } from "@/features/admin/need-mappings/NeedMappingsPage";
// PIM Wave P5 Task 7 — admin changesets (marketplace.attribute.manage).
import { ChangesetsPage } from "@/features/admin/changesets/ChangesetsPage";
// EPIC-010 — Admin OOC + feedback queues (STORY-010-004 / 010-005)
import { OocQueue } from "@/features/admin/ooc/OocQueue";
import { FeedbackQueue } from "@/features/admin/feedback/FeedbackQueue";
// Admin metrics dashboard (STORY-M1-DASH)
import { AdminMetricsDashboard } from "@/features/admin/dashboard/AdminMetricsDashboard";
// P3 admin-wave — AI Consumption Console (ops.metrics.read).
import { AiUsagePage } from "@/features/admin/ai-usage/AiUsagePage";
// Admin-wave — users currently in session, ranked by usage (ops.metrics.read).
import { LiveSessionsPage } from "@/features/admin/live-sessions/LiveSessionsPage";
// EPIC-006 — Seller dashboard (Phase 7)
import { SellerGuard } from "@/features/seller-dashboard/SellerGuard";
import { ProductListPage } from "@/features/seller-dashboard/ProductListPage";
import { ProductEditorPage } from "@/features/seller-dashboard/ProductEditorPage";
// Seller portal shell + pages (Task 6.7)
import { SellerPortalShell } from "@/features/seller-portal/SellerPortalShell";
import { DashboardHome } from "@/features/seller-portal/DashboardHome";
import { SellerSettingsPage } from "@/features/seller-portal/SellerSettingsPage";

/**
 * The tab title follows the UI language (guardrail SUG-004, final web fix
 * round item 14). index.html keeps the static Russian <title> for crawlers
 * and link previews — one URL serves every language and the SPA has no
 * per-route head management — so once the app runs, the title is the active
 * language's `common:meta.title` and changes with every switch (RU's value is
 * index.html's title verbatim; seo-meta.test.ts pins that).
 */
function useDocumentTitle(): void {
  const { t } = useTranslation("common");
  const title = t("meta.title");
  useEffect(() => {
    document.title = title;
  }, [title]);
}

export function App(): ReactElement {
  useDocumentTitle();
  return (
    <Routes>
      {/* Landing v2 — the imported Claude Design composition. Since guest chat
          (SCRUM-275, owner decision 2026-10-07) `/` is the assistant for every
          visitor (HomeRoute, inside the shell below) and the landing lives
          here; HomeRoute forwards to it when guest mode is off. */}
      <Route path="/about" element={<LandingV2 />} />

      {/* EPIC-001 — Auth (STORY-001-009 screens). An already-signed-in user is
          redirected to the dashboard (RedirectIfAuthenticated) so the auth +
          onboarding entry points never re-show a form to a logged-in user. */}
      <Route
        path="/login"
        element={
          // identityAware (SCRUM-1xx): an admin account must be rejected here,
          // not blindly redirected — see RedirectIfAuthenticated's doc comment.
          <RedirectIfAuthenticated identityAware>
            <LoginScreen />
          </RedirectIfAuthenticated>
        }
      />

      {/* Track 0 persona-auth — seller front door (accent branding, OAuth on).
          Tasks 5 (/admin/login) + 6 (guard retargeting) append/adjust here. */}
      <Route
        path="/seller/login"
        element={
          <RedirectIfAuthenticated persona="seller">
            <AuthLoginScreen persona="seller" />
          </RedirectIfAuthenticated>
        }
      />

      {/* Track 0 persona-auth — admin front door (utility branding, OAuth OFF,
          no public signup; consumes the admin-invite redirect params). Declared
          at the top level (outside AdminGuard): the literal /admin/login outranks
          the nested /admin/* splat, and the route must render the login form to a
          logged-out user rather than be gated by the admin guard. This is also
          the live destination of the admin-invite accept redirect
          ({web_origin}/admin/login?invitation_accepted=1[&already_admin][&set_password]),
          previously dead (404). */}
      <Route
        path="/admin/login"
        element={
          <RedirectIfAuthenticated persona="admin">
            <AuthLoginScreen persona="admin" />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfAuthenticated>
            <SignupScreen />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/verify-email"
        element={
          <RedirectIfAuthenticated>
            <VerifyEmailScreen />
          </RedirectIfAuthenticated>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/auth/handoff/:id" element={<OAuthHandoffScreen />} />

      {/* Design-revamp — email-first onboarding wizard (auth surface, no global
          nav). NOT wrapped in RedirectIfAuthenticated: the wizard logs the user
          in at its OTP step and then continues to Basics→Done, so a reactive
          auth guard would eject them mid-wizard. The "already signed-in on
          entry" redirect is handled by a mount-only check inside OnboardingFlow. */}
      <Route path="/onboarding" element={<OnboardingFlow />} />

      {/*
        AppShell — pathless layout route that mounts the GLOBAL NAVIGATION
        (desktop top-nav + mobile bottom tab bar, translated from the design
        prototype's WebTopNav / BottomNav) plus the header search bar, on the
        `surface: 'app'` screens: marketplace (browse + search results), chat,
        and account. The auth routes (login / signup / verify / forgot / reset
        / oauth) + the landing (/about) stay OUTSIDE it (no global nav on those),
        per the design surface meta. Supersedes the search-only SearchBarLayout
        (STORY-003-008).
      */}
      <Route element={<AppShell />}>
        {/* `/` — the chat in guest mode, /chat for a member, /about otherwise
            (SCRUM-275). Inside the shell: a guest gets the full app chrome. */}
        <Route path="/" element={<HomeRoute />} />

        {/* EPIC-001 — Account (STORY-001-010): profile / sessions / privacy. */}
        <Route
          path="/account"
          element={
            <RequireAuth>
              <AccountLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfileScreen />} />
          <Route path="sessions" element={<SessionsScreen />} />
          <Route path="privacy" element={<PrivacyScreen />} />
        </Route>

        {/* EPIC-003 — Marketplace browse (STORY-003-005). Public, no auth. */}
        <Route path="/marketplace" element={<MarketplacePage />} />

        {/* EPIC-003 — Marketplace typed-search results (STORY-003-008). Public. */}
        <Route path="/marketplace/search" element={<SearchResultsPage />} />

        {/* Demo slice — product detail (chat card destination). Public, no auth. */}
        <Route
          path="/marketplace/products/:id"
          element={<ProductDetailPage />}
        />

        {/* EPIC-002 — Chat (STORY-002-009). ChatEntryRoute: a member gets the
            chat; an anonymous visitor goes to `/` in guest mode and to
            /login?return_to otherwise (STORY-002-009 AC12, guest mode off). */}
        <Route path="/chat" element={<ChatEntryRoute />} />

        {/* The captured-renovation "project" dashboard (formerly the consumer
            home at /project) was hidden product-wide 2026-07-06; consumers now
            land on the assistant (DOOR_DEFAULT.consumer = "/chat"). The backend
            projects model/API remain for reversibility. */}

        {/* Design-revamp — Materials dual-pane (auth: the user's quote / Смета). */}
        <Route
          path="/materials"
          element={
            <RequireAuth>
              <MaterialsScreen />
            </RequireAuth>
          }
        />
        {/* A specific saved quote by id. Formerly /project/materials/:id;
            renamed 2026-07-06 when the "project" namespace was retired. */}
        <Route
          path="/materials/:id"
          element={
            <RequireAuth>
              <MaterialsScreen />
            </RequireAuth>
          }
        />

        {/* Design-revamp — Find shops (public seller finder + map). A single
            splat route (not two sibling <Route>s for "/shops" and
            "/shops/sellers/:sellerId") so selecting a seller never remounts
            ShopsScreen/ShopsMap — React Router treats distinct route
            entries as distinct identities and remounts on navigation
            between them, which was resetting the 2GIS map to its default
            zoomed-out center on every seller click (SCRUM-147 follow-up).
            ShopsScreen extracts sellerId itself via useMatch. */}
        <Route path="/shops/*" element={<ShopsScreen />} />

        {/* Design-revamp — Knowledge base (public). */}
        <Route path="/library" element={<LibraryListScreen />} />
        <Route path="/library/:slug" element={<ArticleScreen />} />

        {/* Design-revamp — Settings, the «Ещё» tab. SettingsEntryRoute: a
            member gets SettingsScreen; a visitor in guest mode the guest «Ещё»
            (sign in / sign up, language, theme — SCRUM-276); otherwise
            /login?return_to as before. */}
        <Route path="/settings" element={<SettingsEntryRoute />} />
      </Route>

      {/* EPIC-006 — Seller self-registration wizard (Phase 6, public — no auth). */}
      {/* Start screen (creates a new application). */}
      <Route path="/sellers/register" element={<SellerRegistrationFlow />} />
      {/* Resume / deep-link to a step within a saved application. */}
      <Route path="/sellers/register/:id/*" element={<SellerRegistrationFlow />} />

      {/* EPIC-006 — Seller claim page (auth-required; token from the approval email). */}
      <Route
        path="/sellers/claim"
        element={
          <RequireAuth>
            <ClaimPage />
          </RequireAuth>
        }
      />

      {/* EPIC-006 — Seller portal (Task 6.7): all seller routes nested under
          SellerGuard + SellerPortalShell. /sellers/me renders DashboardHome. */}
      <Route element={<SellerGuard />}>
        <Route element={<SellerPortalShell />}>
          <Route path="/sellers/me" element={<DashboardHome />} />
          <Route path="/sellers/me/products" element={<ProductListPage />} />
          <Route
            path="/sellers/me/products/new"
            element={<ProductEditorPage />}
          />
          <Route
            path="/sellers/me/products/:id"
            element={<ProductEditorPage />}
          />
          <Route path="/sellers/me/settings" element={<SellerSettingsPage />} />
        </Route>
      </Route>

      {/* EPIC-008 — Admin shell (STORY-008-006). AdminGuard (RequireAdminAuth)
          gates entry to ANY admin route; per-screen RequirePermissionRoute gates
          each route by its RBAC slug. Later stories (008-007/008/009/010,
          009-005/007, 010-004/005, 011-008) append their permission-gated routes
          before the /admin/* catch-all (literal paths first, the in-shell 404
          last). */}
      <Route element={<AdminGuard />}>
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboardLanding />} />
          {/* STORY-M1-DASH — real metrics dashboard (ops.metrics.read). */}
          <Route
            path="/admin/dashboard"
            element={
              <RequirePermissionRoute slug="ops.metrics.read">
                <AdminMetricsDashboard />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/admin/seller-applications"
            element={
              <RequirePermissionRoute slug="marketplace.seller.approve">
                <QueueListPage />
              </RequirePermissionRoute>
            }
          />
          {/* PIM Wave P3 Task 3 — product review queue. */}
          <Route
            path="/admin/product-review"
            element={
              <RequirePermissionRoute slug="marketplace.product.approve">
                <ProductReviewPage />
              </RequirePermissionRoute>
            }
          />
          {/* PIM Wave P3 Task 4 — catalog-suggestions review queue. */}
          <Route
            path="/admin/suggestions"
            element={
              <RequirePermissionRoute slug="marketplace.suggestion.review">
                <SuggestionsPage />
              </RequirePermissionRoute>
            }
          />
          {/* EPIC-008 — marketplace management (STORY-008-007). */}
          <Route
            path="/admin/sellers"
            element={
              <RequirePermissionRoute slug="marketplace.seller.read">
                <SellerListPage />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <RequirePermissionRoute slug="marketplace.product.read">
                <AdminProductListPage />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/admin/products/new"
            element={
              <RequirePermissionRoute slug="marketplace.product.edit">
                <AdminProductEditorPage />
              </RequirePermissionRoute>
            }
          />
          {/* SCRUM-181 — the bare id URL is what the editor's own breadcrumb
              links to after a save. Without this it fell through to the
              /admin/* splat and dead-ended on the in-shell 404. Canonicalise
              onto .../edit rather than mounting the editor twice. */}
          <Route
            path="/admin/products/:id"
            element={<Navigate to="edit" replace relative="path" />}
          />
          <Route
            path="/admin/products/:id/edit"
            element={
              <RequirePermissionRoute slug="marketplace.product.edit">
                <AdminProductEditorPage />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequirePermissionRoute slug="users.read">
                <UserListPage />
              </RequirePermissionRoute>
            }
          />
          {/* STORY-010-004 — admin OOC triage queue (ooc.read). */}
          <Route
            path="/admin/ooc"
            element={
              <RequirePermissionRoute slug="ooc.read">
                <OocQueue />
              </RequirePermissionRoute>
            }
          />
          {/* STORY-010-005 — admin feedback queue + reply composer (feedback.read). */}
          <Route
            path="/admin/feedback"
            element={
              <RequirePermissionRoute slug="feedback.read">
                <FeedbackQueue />
              </RequirePermissionRoute>
            }
          />
          {/* STORY-008-009 — feature-flags management (ops.feature_flags.manage). */}
          <Route
            path="/admin/feature-flags"
            element={
              <RequirePermissionRoute slug="ops.feature_flags.manage">
                <FeatureFlagsPage />
              </RequirePermissionRoute>
            }
          />
          {/* P0 admin kit — dev/demo gallery (not in nav). */}
          <Route
            path="/admin/kit-gallery"
            element={
              <RequirePermissionRoute slug="ops.metrics.read">
                <AdminKitGallery />
              </RequirePermissionRoute>
            }
          />
          {/* App Config (UI for shipped STORY-008-004). Read = ops.app_config.read. */}
          <Route
            path="/admin/app-config"
            element={
              <RequirePermissionRoute slug="ops.app_config.read">
                <AppConfigPage />
              </RequirePermissionRoute>
            }
          />
          {/* STORY-008-008 — audit-log viewer (ops.audit.read). READ-only. */}
          <Route
            path="/admin/audit-log"
            element={
              <RequirePermissionRoute slug="ops.audit.read">
                <AuditLogPage />
              </RequirePermissionRoute>
            }
          />
          {/* P1b admin-wave — categories CRUD (marketplace.category.manage). */}
          <Route
            path="/admin/categories"
            element={
              <RequirePermissionRoute slug="marketplace.category.manage">
                <CategoryListPage />
              </RequirePermissionRoute>
            }
          />
          {/* PIM Wave P1 — attribute field library (marketplace.attribute.manage). */}
          <Route
            path="/admin/attribute-fields"
            element={
              <RequirePermissionRoute slug="marketplace.attribute.manage">
                <FieldLibraryPage />
              </RequirePermissionRoute>
            }
          />
          {/* PIM Wave P1 — category binding editor (marketplace.attribute.manage). */}
          <Route
            path="/admin/categories/:id/fields"
            element={
              <RequirePermissionRoute slug="marketplace.attribute.manage">
                <BindingEditorPage />
              </RequirePermissionRoute>
            }
          />
          {/* PIM Wave P4 Task 7 — need-mappings CRUD (marketplace.attribute.manage). */}
          <Route
            path="/admin/need-mappings"
            element={
              <RequirePermissionRoute slug="marketplace.attribute.manage">
                <NeedMappingsPage />
              </RequirePermissionRoute>
            }
          />
          {/* PIM Wave P5 Task 7 — changesets (marketplace.attribute.manage). */}
          <Route
            path="/admin/changesets"
            element={
              <RequirePermissionRoute slug="marketplace.attribute.manage">
                <ChangesetsPage />
              </RequirePermissionRoute>
            }
          />
          {/* P1b admin-wave — markets CRUD (marketplace.market.manage). */}
          <Route
            path="/admin/markets"
            element={
              <RequirePermissionRoute slug="marketplace.market.manage">
                <MarketListPage />
              </RequirePermissionRoute>
            }
          />
          {/* P1b admin-wave — bulk import (marketplace.bulk_import). */}
          <Route
            path="/admin/import/sellers-and-products"
            element={
              <RequirePermissionRoute slug="marketplace.bulk_import">
                <BulkImportPage />
              </RequirePermissionRoute>
            }
          />
          {/* P1b admin-wave — invite-admin (users.invite_admin). */}
          <Route
            path="/admin/invitations"
            element={
              <RequirePermissionRoute slug="users.invite_admin">
                <InvitationsPage />
              </RequirePermissionRoute>
            }
          />
          {/* P3 admin-wave — AI Consumption Console (ops.metrics.read). */}
          <Route
            path="/admin/ai-usage"
            element={
              <RequirePermissionRoute slug="ops.metrics.read">
                <AiUsagePage />
              </RequirePermissionRoute>
            }
          />
          {/* Admin-wave — users currently in session, ranked by usage
              (ops.metrics.read — same gate as the sibling metrics tiles). */}
          <Route
            path="/admin/live-sessions"
            element={
              <RequirePermissionRoute slug="ops.metrics.read">
                <LiveSessionsPage />
              </RequirePermissionRoute>
            }
          />
          {/* SCRUM-182 — every admin manages their own account, so this one
              carries no RBAC slug gate (unlike the ops pages above). */}
          <Route path="/admin/account" element={<AdminAccountPage />} />
          <Route path="/admin/*" element={<AdminNotFound />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
